<template>
<div class="min-vh-100">
    <div class="row">
        <div class="col-12 mb-3">
            <div id="rfx-manage-criteria-overall-settings-row" class="row" :class="{'scoring-active': overallEnabled}">
                <div class="col-12">
                    <div class="d-flex justify-content-center">
                        <div class="max-width-700">
                            <div :class="overallEnabled ? 'py-3' : 'py-5'" style="transition: 0.2s;">
                                <h5>Setup score sheet for this event?</h5>
                                <p class="text-muted">
                                    Decision criteria is optional, and can be enabled later after you launch the event.
                                </p>
                            </div>
                            <div class="d-flex justify-content-center">
                                <base-item-picker
                                    :value="overallEnabled"
                                    :options="overallEnabledOptions"
                                    :loading="loading || loadingSettings"
                                    @input="handleSettingsChange(null, null, $event, null)"
                                ></base-item-picker>
                                <div class="d-flex align-items-center h-100 px-3 max-width-250">
                                    <b-form-checkbox v-model="criteriaShowVendors" :disabled="!overallEnabled" @change="handleSettingsChange(null, null, null, $event)">
                                        <div>Show score sheet items to vendors?</div>
                                        <p class="mb-0 text-muted small">Only the <b>criteria</b> will be displayed.  The actual scores will never be shown to vendors.</p>
                                    </b-form-checkbox>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="rfxHasExistingScores" class="col-12">
                    <div class="my-4 d-flex">
                        <b-icon icon="exclamation-triangle" variant="danger" class="mr-2 h4"></b-icon>
                        <h6>
                            WARNING:  Some Vendor Responses have already been scored based on existing criteria.  If you add new criteria, you will have to re-score those responses.
                        </h6>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="d-none col-4">
                    <b-form-group
                        label="Scoring System"
                        class="spl-inline-form mb-0"
                        label-cols="auto"
                    >
                        <b-form-select
                            v-model="scoringSystem" 
                            :options="scoringOptions"
                        >
                            <template #first>
                                <b-form-select-option value="" disabled>Select...</b-form-select-option>
                            </template>
                        </b-form-select>
                    </b-form-group>    
                    <b-form-group
                        label="Who completes this?"
                        class="spl-inline-form mb-0"
                        label-cols="auto"
                    >
                        <b-form-select
                            v-model="scoringPeople" 
                            :options="scoringPeopleOptions"
                        >
                            <template #first>
                                <b-form-select-option value="" disabled>Select...</b-form-select-option>
                            </template>
                        </b-form-select>
                    </b-form-group>    
                    <b-form-checkbox>Show Criteria to Vendors?</b-form-checkbox>
                    <div class="small text-muted">Scores are never shown to vendors.</div>
                    <h5>Make import button to import from Question Lists</h5>
                </div>
            </div>
        </div>

        <transition name="custom-scoresheet-animation" enter-active-class="animate__animated animate__fadeInUp animate__fast" leave-active-class="animate__animated animate__fadeOutDown animate__fast">
        <div v-if="overallEnabled" class="col-12 spl-inputs-minimal">

            <h3>Score Sheet</h3>

            <div class="bg-gray-50 border rounded-lg p-3 shadow-sm">
                <div class="d-flex justify-content-between align-items-end mb-2">
                    <div class="d-flex align-items-center">
                        <h4 class="d-inline-block mb-0">Minimum Qualifications</h4>
                        <b-form-group
                            label=""
                            label-cols="auto"
                            label-class="p-0"
                            class="ml-4 mb-0 font-size-100"
                        >
                            <b-form-checkbox switch size="lg" v-model="requirementsEnabled" @change="handleSettingsChange($event, null, null, null)">
                                <span class="font-size-100">{{requirementsEnabled ? 'On' : 'Off'}}</span>
                            </b-form-checkbox>
                        </b-form-group>
                    </div>
                    <div>
                        <div v-if="!loading && !loadingSettings && requirementsEnabled" class="text-right font-weight-bold small">
                            <transition name="fade-fast" mode="out-in">
                                <span v-if="allReqsValid" key="reqs-valid" class="text-success opacity-60">
                                    <b-icon icon="check-circle" class="mr-1"></b-icon>
                                    OK
                                </span>
                                <span v-else key="reqs-invalid" class="text-danger">
                                    <b-icon icon="exclamation-circle" class="mr-1"></b-icon>
                                    Fix Errors
                                </span>
                            </transition>
                        </div>
                    </div>
                </div>
                <base-single-collapse
                    button-text="Instructions"
                    class="mb-2"
                >
                    <ul class="list-flush mb-0">
                        <li><b>Minimum Qualifications</b> are required qualifications that must be met for a vendor to be considered.</li>
                        <li>These do not factor into the vendor's score, but failing to meet any requirement will disqualify a vendor.</li>
                        <li>When writing your requirements, word them such that a "yes" answer means the vendor meets the requirement or qualification.</li>
                    </ul>
                </base-single-collapse>
                <transition name="scale-in-out" mode="out-in">
                    <div v-if="requirementsEnabled">
                        <transition name="fade-fast" mode="out-in">
                            <div v-if="loading" key="requirement-loading">
                                <busy-spinner text="Loading..."></busy-spinner>
                            </div>
                            <draggable
                                v-else
                                key="requirement-list"
                                id="minimum-reqs-draggable"
                                :list="minimumReqs"
                                class="list-group shadow-sm mb-2"
                                draggable=".minimum-req"
                                group="minimumReqs"
                                animation="250"
                                @sort="handleSort($event, minimumReqs, null)"
                            >
                                <div 
                                    v-for="(req, reqIndex) in minimumReqs"
                                    :key="req.id"
                                    class="minimum-req list-group-item px-0 py-2 bg-gray-100"
                                >
                                    <div class="d-flex justify-content-between align-items-center">
                                        <div class="width-50 drag-handle text-center text-muted">
                                            <b-icon icon="justify" font-scale="1.5"></b-icon>
                                        </div>
                                        <b-form-input 
                                            v-model="req.name" 
                                            type="text"
                                            lazy
                                            required
                                            :state="!!req.name ? null : false"
                                            placeholder="Enter requirement..."
                                            :class="req.name ? '' : 'empty-value'"
                                        ></b-form-input>
                                        <div class="width-40 px-1 text-center">
                                            <b-button size="sm" variant="minimal-danger" class="" @click="removeItem(minimumReqs, reqIndex, null)">
                                                <b-icon icon="x"></b-icon>
                                            </b-button>
                                        </div>
                                    </div>
                                </div>
                            </draggable>
                        </transition>
                        <div class="d-flex justify-content-between align-items-center">
                            <b-button size="sm" variant="sharp-primary" @click="addRequirement">
                                <b-icon icon="plus" class="mr-1"></b-icon>
                                Add Requirement
                            </b-button>
                            <div v-if="minimumReqs && minimumReqs.length" class="font-size-80 text-muted">
                                {{ minimumReqs.length }} Minimum Qualification<span v-if="minimumReqs.length !== 1">s</span>
                            </div>
                        </div>
                        
                    </div>
                </transition>
            </div>

            <hr>

            <div class="bg-gray-50 border rounded-lg p-3 shadow-sm">
                <div class="d-flex justify-content-between align-items-end mb-2">
                    <div class="d-flex align-items-center mb-2">
                        <h4 class="d-inline-block mb-0">Scoring Criteria</h4>
                        <b-form-group
                            label=""
                            label-cols="auto"
                            label-class="p-0"
                            class="ml-4 mb-0 font-size-100"
                        >
                            <b-form-checkbox switch size="lg" v-model="scoringEnabled" @change="handleSettingsChange(null, $event, null, null)">
                                <span class="font-size-100">{{scoringEnabled ? 'On' : 'Off'}}</span>
                            </b-form-checkbox>
                        </b-form-group>
                        
                    </div>
                    <div class="d-flex">
                        <div v-if="!loading && !loadingSettings && scoringEnabled" class="text-right">
                            <div class="font-weight-bold small">
                                <transition name="fade-fast" mode="out-in">
                                    <span v-if="allScoreCriteriaValid" key="valid" class="text-success opacity-60">
                                        <b-icon icon="check-circle" class="mr-1"></b-icon>
                                        All Criteria OK
                                    </span>
                                    <span v-else key="invalid" class="text-danger">
                                        <b-icon icon="exclamation-circle" class="mr-1"></b-icon>
                                        Fix Criteria Errors
                                    </span>
                                </transition>
                            </div>
                            <div class="font-weight-bold small">
                                <transition name="fade-fast" mode="out-in">
                                    <span v-if="allWeightsValid" key="valid" class="text-success opacity-60">
                                        <b-icon icon="check-circle" class="mr-1"></b-icon>
                                        All Weights OK
                                    </span>
                                    <span v-else key="invalid" class="text-danger">
                                        <b-icon icon="exclamation-circle" class="mr-1"></b-icon>
                                        Fix Weight Errors
                                    </span>
                                </transition>
                            </div>
                        </div>
                        <!-- <b-button
                            variant="sharp-success"
                            class="ml-3"
                            :disabled="!everythingValid"
                            @click="startSave"
                        >
                            Save Criteria
                        </b-button> -->
                    </div>
                </div>
                <base-single-collapse
                    button-text="Instructions"
                    class="mb-2"
                >
                    <ul class="list-flush mb-0">
                        <li>Scoring Criteria is used as part of your decision-making process to improve the objective comparison between supplier responses.</li>
                        <li>You can create a custom list of scoring criteria, assign weights to each, then assign separate scores for each supplier response.</li>
                        <li>The system will automatically calculate one combined score for each vendor based on the criteria weights and your assigned scores.</li>
                    </ul>
                </base-single-collapse>

                <transition name="scale-in-out" mode="out-in">
                    <div v-if="scoringEnabled">

                        <transition name="fade-fast" mode="out-in">
                            <div v-if="loading" key="criteria-loading">
                                <busy-spinner text="Loading..."></busy-spinner>
                            </div>
                            <div v-else key="scoring-criteria-list">  <!-- criteria area to show when not loading -->
                                <draggable
                                    id="criteria-groups-draggable"
                                    :list="nestedCriteria"
                                    class=""
                                    handle=".group-drag-handle"
                                    draggable=".criteria-group"
                                    group="groups"
                                    animation="250"
                                    @sort="handleSort($event, nestedCriteria, null)"
                                >
                                    <div 
                                        v-for="(cat, catIndex) in nestedCriteria"
                                        :key="cat.id"
                                        class="criteria-group bg-gray-100 border rounded shadow-sm mt-3 d-flex"
                                    >
                                        <div class="group-drag-handle bg-gray-200 py-2 width-50 d-flex flex-column align-items-center">
                                            <b-icon icon="justify" variant="secondary" font-scale="1.8" class="flex-grow-1"></b-icon>
                                            <b-button variant="minimal-danger" @click="removeGroup(nestedCriteria, catIndex, cat.name)">
                                                <b-icon icon="trash" font-scale="1"></b-icon>
                                            </b-button>
                                        </div>
                                        <div class="px-3 py-2 flex-grow-1">
                                            <div class="row">
                                                <div class="col-9">
                                                    <div class="d-flex flex-column h-100 mb-2">
                                                        <click-edit-input
                                                            v-model="cat.name"
                                                            :label-cols="0"
                                                            label="Group Name"
                                                            hide-label
                                                            class="h4 mb-0"
                                                        ></click-edit-input>
                                                        <div class="d-flex">
                                                            <div class="min-width-150"></div>  <!-- Spacing placeholder -->
                                                            <div class="width-350">  <!-- width should match totals row -->
                                                                <b-form inline>
                                                                    <b-form-group
                                                                        label="Group Weight %"
                                                                        class="spl-inline-form"
                                                                        label-cols="auto"
                                                                        label-align="right"
                                                                    >
                                                                        <b-input-group class="min-width-120 max-width-150">
                                                                            <b-input-group-prepend>
                                                                                <b-button 
                                                                                    size="sm" 
                                                                                    text="Button" 
                                                                                    variant="gray-primary" 
                                                                                    class="border-0"
                                                                                    @click="cat.weight--"
                                                                                >
                                                                                    <b-icon icon="dash"></b-icon>
                                                                                </b-button>
                                                                            </b-input-group-prepend>
                                                                            <b-form-input 
                                                                                v-model="cat.weight" 
                                                                                number
                                                                                lazy
                                                                                min="1"
                                                                                max="100"
                                                                                step="1"
                                                                                class="text-center bg-white"
                                                                                :class="totalIsValid(nestedCriteria) ? null : 'text-danger'"
                                                                            ></b-form-input>
                                                                            <b-input-group-append>
                                                                                <b-button 
                                                                                    size="sm" 
                                                                                    text="Button" 
                                                                                    variant="gray-primary" 
                                                                                    class="border-0"
                                                                                    @click="cat.weight++"
                                                                                >
                                                                                    <b-icon icon="plus"></b-icon>
                                                                                </b-button>
                                                                            </b-input-group-append>
                                                                        </b-input-group>
                                                                    </b-form-group>
                                                                </b-form>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="col-3">
                                                    <div class="text-right">
                                                        <div class="font-weight-bold small">
                                                            <transition name="fade-fast" mode="out-in">
                                                                <span v-if="listCriteriaValid(cat.items)" key="valid" class="text-success opacity-60">
                                                                    <b-icon icon="check-circle" class="mr-1"></b-icon>
                                                                    Group Criteria OK
                                                                </span>
                                                                <span v-else key="invalid" class="text-danger">
                                                                    <b-icon icon="exclamation-circle" class="mr-1"></b-icon>
                                                                    Fix Criteria Errors
                                                                </span>
                                                            </transition>
                                                        </div>
                                                        <div class="font-weight-bold small">
                                                            <transition name="fade-fast" mode="out-in">
                                                                <span v-if="totalIsValid(cat.items)" key="valid" class="text-success opacity-60">
                                                                    <b-icon icon="check-circle" class="mr-1"></b-icon>
                                                                    Group Weights OK
                                                                </span>
                                                                <span v-else key="invalid" class="text-danger">
                                                                    <b-icon icon="exclamation-circle" class="mr-1"></b-icon>
                                                                    Fix Weight Errors
                                                                </span>
                                                            </transition>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="row">
                                                <div class="col-12">
                                                    <div class="d-flex align-items-stretch">
                                                        <div class="w-100">
                                                            <div class="d-flex justify-content-between align-items-center px-0 py-2 text-muted font-weight-bold text-uppercase font-size-80">
                                                                <div class="width-50"></div>
                                                                <div class="d-flex justify-content-between align-items-center flex-grow-1">
                                                                    <div class="w-50 pr-2">
                                                                        Criteria Item
                                                                    </div>
                                                                    <div class="w-25 px-2">
                                                                        Rating Type
                                                                        <base-popover-icon 
                                                                            title="Rating Type" 
                                                                            class="ml-1"
                                                                        >
                                                                            <p>
                                                                                <span class="font-weight-bold">5-Point Scale:</span>
                                                                                Rated from 1-5 with 1 = Bad (no points) and 5 = Good (full points).
                                                                            </p>
                                                                            <p>
                                                                                <span class="font-weight-bold">Binary:</span>
                                                                                Rated only 'Yes' or 'No'.  Yes is considered 'good' and gives full score.  No is considered 'bad' and gives score of zero.
                                                                            </p>
                                                                        </base-popover-icon>
                                                                    </div>
                                                                    <div class="w-25 px-2 text-center d-flex align-items-end">
                                                                        <div class="text-center flex-grow-1">
                                                                            Weight %
                                                                        </div>
                                                                        <div class="small pl-2" style="min-width: 50px;">
                                                                            overall
                                                                            <base-popover-icon
                                                                                title="Overall Weight %"
                                                                                class="ml-1"
                                                                            >
                                                                                <p>Overall weight of this item relative to all criteria.</p>
                                                                                <p>All criteria item weights and group weights must be valid (add to 100%) for this to be shown.</p>
                                                                            </base-popover-icon>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div class="width-40 px-1 text-center"></div>
                                                            </div>
                                                            <draggable
                                                                id="first"
                                                                :list="cat.items"
                                                                class="list-group shadow-sm"
                                                                handle=".drag-handle"
                                                                draggable=".item"
                                                                group="a"
                                                                v-bind="draggableOptions"
                                                                @sort="handleSort($event, cat.items, cat.id)"
                                                            >
                                                                <div
                                                                    class="list-group-item item px-0 py-2 bg-gray-50"
                                                                    v-for="(item, itemIndex) in cat.items"
                                                                    :key="item.id"
                                                                >
                                                                    <div class="d-flex justify-content-between align-items-center">
                                                                        <div class="width-50 drag-handle text-center text-muted">
                                                                            <b-icon icon="justify" font-scale="1.5"></b-icon>
                                                                        </div>
                                                                        <div class="d-flex justify-content-between align-items-center flex-grow-1">
                                                                            <div class="w-50 pr-2">
                                                                                <b-form-input 
                                                                                    v-model="item.name" 
                                                                                    type="text"
                                                                                    lazy
                                                                                    required
                                                                                    :state="!!item.name ? null : false"
                                                                                    placeholder="Enter criteria..."
                                                                                    :class="item.name ? '' : 'empty-value'"
                                                                                ></b-form-input>
                                                                            </div>
                                                                            <div class="w-25 px-2">
                                                                                <b-form-select
                                                                                    v-model="item.rating_type" 
                                                                                    :options="ratingTypes"
                                                                                >
                                                                                    <template #first>
                                                                                        <b-form-select-option value="" disabled>Select...</b-form-select-option>
                                                                                    </template>
                                                                                </b-form-select>
                                                                            </div>
                                                                            <div class="w-25 pl-2 d-flex align-items-center">
                                                                                <b-input-group
                                                                                    class="flex-grow-1"
                                                                                >
                                                                                    <b-input-group-prepend>
                                                                                        <b-button 
                                                                                            size="sm" 
                                                                                            text="Button" 
                                                                                            variant="gray-primary" 
                                                                                            class="border-0"
                                                                                            @click="item.weight--"
                                                                                        >
                                                                                            <b-icon icon="dash"></b-icon>
                                                                                        </b-button>
                                                                                    </b-input-group-prepend>
                                                                                    <b-form-input 
                                                                                        v-model="item.weight" 
                                                                                        number
                                                                                        lazy
                                                                                        min="1"
                                                                                        max="100"
                                                                                        step="1"
                                                                                        class="text-center"
                                                                                        :class="totalIsValid(cat.items) ? '' : 'text-danger'"
                                                                                    ></b-form-input>
                                                                                    <b-input-group-append>
                                                                                        <b-button 
                                                                                            size="sm" 
                                                                                            text="Button" 
                                                                                            variant="gray-primary" 
                                                                                            class="border-0"
                                                                                            @click="item.weight++"
                                                                                        >
                                                                                            <b-icon icon="plus"></b-icon>
                                                                                        </b-button>
                                                                                    </b-input-group-append>
                                                                                </b-input-group>
                                                                                <div class="small pl-2" style="min-width: 50px;">
                                                                                    <span v-if="allWeightsValid">{{ percentMaker(itemOverallWeight(item.weight, cat)) }}</span>
                                                                                    <span v-else class="text-danger">---</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div class="width-40 px-1 text-center">
                                                                            <b-button size="sm" variant="minimal-danger" class="" @click="removeItem(cat.items, itemIndex, cat.id)">
                                                                                <b-icon icon="x"></b-icon>
                                                                            </b-button>
                                                                        </div>
                                                                        
                                                                    </div>
                                                                    
                                                                </div>
                                                            </draggable>
                                                            <div class="d-flex justify-content-between align-items-start px-0 py-1">
                                                                <div class="width-50"></div>
                                                                <div class="d-flex justify-content-between align-items-start flex-grow-1">
                                                                    <div class="w-50 pr-2 d-flex justify-content-between">
                                                                        <b-button size="sm" variant="sharp-primary" @click="addItem(cat.items, cat.id)">
                                                                            <b-icon icon="plus" class="mr-1"></b-icon>
                                                                            Add Item
                                                                        </b-button>
                                                                        <div>
                                                                            <!-- placeholder -->
                                                                        </div>
                                                                    </div>
                                                                    <div class="w-25 text-right">
                                                                        <b-button size="sm" variant="minimal-primary" class="no-outline" @click="splitWeights(cat.items)">
                                                                            <b-icon icon="lightning-fill" class="mr-1"></b-icon>
                                                                            Split item % equally &rarr;
                                                                            <!--<b-icon icon="arrow-right" class="ml-1"></b-icon>-->
                                                                        </b-button>
                                                                    </div>
                                                                    <div class="w-25 px-2 text-center d-flex">
                                                                        <div class="flex-grow-1">
                                                                            <div 
                                                                                :class="totalIsValid(cat.items) ? 'text-success' : 'text-danger'"
                                                                                class="font-weight-bold font-size-110"    
                                                                            >
                                                                                {{itemTotal(cat.items)}}%
                                                                            </div>
                                                                            <div class="small">Total must be 100%</div>
                                                                        </div>
                                                                        <div class="small pl-2" style="min-width: 50px;">
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div class="width-40 px-1 text-center">
                                                                </div>
                                                            </div>
                                                        </div>

                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </draggable>
                                <div class="mt-2 d-flex align-items-start">
                                    <div class="min-width-200">
                                        <b-button variant="sharp-primary" @click="addGroup">
                                            <b-icon icon="plus" class="mr-1"></b-icon>
                                            Add Group
                                        </b-button>
                                    </div>
                                    <div class="d-flex width-400">
                                        <b-button size="sm" variant="minimal-primary" class="no-outline" @click="splitWeights(nestedCriteria)">
                                            <div class="d-flex align-items-start">
                                                <div class="text-right">Split group<br>weights equally</div>
                                                <b-icon icon="lightning-fill" class="mx-2"></b-icon>
                                                <b-icon icon="arrow-right"></b-icon>
                                            </div>
                                        </b-button>
                                        <div class="text-center">
                                            <div
                                                :class="totalIsValid(nestedCriteria) ? 'text-success' : 'text-danger'"
                                                class="font-weight-bold font-size-110"    
                                            >
                                                {{itemTotal(nestedCriteria)}}%
                                            </div>
                                            <div class="small">Total must be 100%</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </transition>
                    </div>
                </transition>
            </div>
        </div>
        </transition>
    </div>
</div>
</template>

<script>
import draggable from 'vuedraggable';
import { ClickEditInput } from '../js/custom-form-components'
import { suppliosApi } from '../js/api-service';
//import groupBy from 'lodash/groupBy';
//import partition from 'lodash/partition';
//import pick from 'lodash/pick';
import cloneDeep from 'lodash/cloneDeep';
import has from 'lodash/has';
import isEqual from 'lodash/isEqual';
//import uniqueId from 'lodash/uniqueId'
import { buildNestedCriteria } from '../js/scoring-criteria';

/*
const baseCriteria = {  // criteria object that all types are based on, we'll use Object.assign() when loading from API and making new
    name: '',
    id: null,
    local_id: null,
    existing: false,
    weight: 100,
    rating_type: 'BINARY',  // makes no sense for group, but it is on model
    is_requirement: false,  // this is what tells us this is a requirement, not scoring item
    is_parent: false,
    position: 0,
    parent: null, 
    formatForApi: function() {
        let baseReturn = {
            name: this.name,
            id: this.id,
            local_id: this.local_id,
            existing: this.existing,
            weight: this.weight,
            rating_type: this.rating_type,  // makes no sense for group, but it is on model
            is_requirement: this.is_requirement,  // this is what tells us this is a requirement, not scoring item
            is_parent: this.is_parent,
            position: this.position,
            parent: this.parent, 
        };
        return baseReturn;
    },
};*/

export default {
    name: 'RfxManageCriteria',
    components: {
        draggable,
        ClickEditInput,
    },
    props: {
        rfxPk: {
            type: [String, Number],
            required: true,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error while working with criteria lists.',
            error: false,
            loading: false,
            rfxHasExistingScores: false,  // if RFX already has score sheets open for it, this will be true... then you could disable making new criteria
            groupTotalMax: 100,  // the max % or points for each group
            scoringSystem: 'weight',
            // for overall settings:
            overallEnabled: null,  // v-model for overall enable/disable selector - set to null initially, then will get to to T/F when we get data from API based on whether requirements or scoring is enabled
            overallEnabledOptions: [
                {
                    value: false,
                    title: 'None',
                    text: 'Do not use a score sheet or decision criteria for this event.',
                },
                {
                    value: true,
                    title: 'Use Score Sheet',
                    text: 'Create a score sheet with minimum qualifications and/or scoring criteria.',
                },
            ],
            criteriaShowVendors: false,  // should the criteria be shown to vendors?
            loadingSettings: false,  // busy indicator when updating toggles
            scoringEnabled: false,  
            requirementsEnabled: false,
            scoringOptions: [
                {
                    value: 'weight',
                    text: 'Percent Weights',
                },
                {
                    value: 'points',
                    text: 'Total Points'
                },
            ],
            ratingTypes: [
                {
                    value: 'FIVE',
                    text: '5-Point Scale',
                },
                {
                    value: 'BINARY',
                    text: 'Binary (Yes/No)'
                },
            ],
            scoringPeople: '',
            scoringPeopleOptions: [
                {
                    value: 'owner',
                    text: 'Event Owner',
                },
                {
                    value: 'team',
                    text: 'All Team Members'
                },
            ],
            draggableOptions: {
                animation: 200,
            },
            newReqId: 1,
            newItemId: 1,
            newGroupId: 1,
            minimumReqs: [],  // cloneDeep from defaultMinimumReqs on created hook
            defaultMinimumReqs: [   // requirements array will go here, keep one item here as placeholder for empty case
                {
                    name: '',
                    id: 'newreq0',
                    existing: false,
                    weight: 0,  // can't set this to null, otherwise invalid on backend
                    rating_type: 'BINARY',
                    is_requirement: true,  // this is what tells us this is a requirement, not scoring item
                    is_parent: false,
                    position: 0,
                    parent: 'parent',  // set to 'parent' which you will set to null before sending to API
                },
            ],  
            nestedCriteria: [],  // cloneDeep from defaultNestedCriteria on created hook
            defaultNestedCriteria: [  // nested criteria list will go here after we build it, keep one group & one criteria here for empty case
                {
                    name: 'Score Group',
                    id: 'newgroup0',
                    existing: false,
                    weight: 100,
                    rating_type: 'BINARY',  // makes no sense for group, but it is on model
                    is_requirement: false,  // this is what tells us this is a requirement, not scoring item
                    is_parent: true,
                    position: 0,
                    parent: 'parent',  // set to 'parent' which you will convert back to 'null' before sending back
                    items: [
                        {
                            name: '',
                            id: 'newitem0',
                            existing: false,
                            weight: 100,
                            rating_type: 'FIVE',
                            is_requirement: false,
                            is_parent: false,
                            position: 0,
                            parent: 'newgroup0',
                        },
                    ],
                },
            ],  
            initialLoadComplete: false,  // set to true as soon as you load initial data, part of checking if we have unsaved data
            dataLastSaved: {},  // stash data here after getting from API... after all formatting, in the exact format you will later send back to API
            proceedUnsavedChanges: false,  // flag to override blocking leaving on unsaved changes, checked during this.okToContinue().  set to false initially, then you set to true inside this.handleUnsavedData() if user clicks on discard changes
        }
    },
    computed: {
        hasAnyRequirements: function() {
            return this.minimumReqs.some(req => !!req.name);  // check if any of the requirements have 'name' entered
        },
        hasAnyScoreCriteria: function() {
            // return true if you have at least one NON-PARENT scoring criteria with some text entered, even if not fully valid, used to know if user entered something worth keeping
            return this.nestedCriteria.every(grp => {  // loop through all groups
                return grp.items.some(item => !!item.name);  // in each group's items, check if any of them have 'name' entered
            });
        },
        allCriteriaDisabled: function() {
            return !this.requirementsEnabled && !this.scoringEnabled;
        },
        allReqsValid: function() {
            // checks if all requirements are OK
            if (!Array.isArray(this.minimumReqs)) return true;  // return true if there is no requirements list at all
            if (this.minimumReqs.length < 1) return true;  // if nothing in the array, return true
            return this.minimumReqs.every(req => {
                return this.singleRequirementValid(req);  // use requirement validator method
            });
        },
        allWeightsValid: function() {
            // checks if weights are valid for scoring criteria
            let topLevelValid = this.totalIsValid(this.nestedCriteria);  // checks if top level group % splits are valid
            let allGroupsValid = this.nestedCriteria.every(cat => {
                return this.totalIsValid(cat.items);  // check if each groups items list is valid
            });
            return topLevelValid && allGroupsValid;
        },
        allScoreCriteriaValid: function() {
            // check all non-weight things for each scoring criteria -- checks only scoring criteria!
            return this.nestedCriteria.every(cat => {
                let topLevelValid = !!cat.name && cat.weight >= 0 && cat.weight <= 100;  // will return true if non empty string
                let itemsValid;
                if (cat.items && cat.items.length > 0) {
                    itemsValid = cat.items.every(item => {
                        return this.singleCriteriaValid(item); // use our base scoring criteria item valid method
                        //return !!item.name && !!item.rating_type && item.weight >= 0 && item.weight <= 100;
                    });
                } else {
                    itemsValid = false;
                }
                return topLevelValid && itemsValid;
            });
        },
        allScoringValid: function() {
            // are all scoring criteria and weights valid?
            return this.allScoreCriteriaValid && this.allWeightsValid;
        },
        allSettingsValid: function() {
            // some simple checks on whether boolean settings are valid
            // just check that this.criteriaShowVendors is false if all criteria is disabled
            let showVendorsValid = this.allCriteriaDisabled ? !this.criteriaShowVendors : true;
            return showVendorsValid;
        },
        /* everythingValid: function() {
            // have to account for some criteria type being disabled -- if one type is not enabled, ignore it and consider it valid
            let overallEnabledMakesSense = this.overallEnabled ? !this.allCriteriaDisabled : true;  // if overallEnabled is ON, but user did not enable reqs or scoring, show error modal and prompt user to disable things (better UX)
            let scoringValid = this.scoringEnabled ? this.allScoringValid : true;  
            let reqsValid = this.requirementsEnabled ? this.allReqsValid : true;
            let settingsValid = this.allSettingsValid;
            return overallEnabledMakesSense && scoringValid && reqsValid && settingsValid;
        },        */
    },
    created: function() {
        // set initial things
        this.minimumReqs = cloneDeep(this.defaultMinimumReqs);
        this.nestedCriteria = cloneDeep(this.defaultNestedCriteria);
    },
    mounted: function() {
        this.getCriteria();
        this.updateList(this.minimumReqs, null);  // set initial positions for minimum requirements list
        this.updateList(this.nestedCriteria, null);  // set initial positions for top level groups / categories
        this.nestedCriteria.forEach(cat => this.updateList(cat.items, cat.id));  // set initial positions for category items
    },
    methods: {
        // ==== PUBLIC FUNCTIONS TO CALL FROM PARENT FORM WIZARD 
        saveAll: function() {
            // need this method so you can call it from setup wizard
            // must return a promise that resolves to True if save is OK, false if not OK
            //if (!this.scoringEnabled && !this.requirementsEnabled) return Promise.resolve(true);  // if none of this is enabled, just return promise that resolves to true
            //return this.startSave();  // returns a Promise that resolves to Boolean
            let saveStatusPromise = this.startSave();
            return {
                statusPromise: saveStatusPromise,
                successMessage: 'Score Sheet settings saved',
            };
        },
        okToContinue: function() {
            // function to be called by parent that ties together canLeave and handleUnsavedData
            // takes 1 arg... the bvEvt tab change event, which you can cancel using preventDefault(), but must do so before calling any other async functions
            // should return a PROMISE resolving to Boolean for whether its OK to continue (true = continue)
            if (this.proceedUnsavedChanges) return Promise.resolve(true);  // the override we set if user selects to not save changes
            let hasUnsaved = this.hasUnsavedCheck();  // returns simple Boolean where true = has unsaved changes
            if (!hasUnsaved) return Promise.resolve(true);  // if no unsaved changes, then continue
            // now handle unsaved changes, 
            // cancel the tabChangeEvent & show the modal to let user decide how to respond and return that promise
            // no longer doing it this way... tabChangeEvent.preventDefault();  // have to call this before you call any other async functions!  
            return this.handleUnsavedData();  
        },
        showSaveSuccess: function(msg) {
            // separating out save success message toast for DRY
            let successMsg = msg ? msg : 'Score Sheet settings saved';
            this.$toast.success(successMsg);
        },
        /// above is public, below is private
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        buildNestedCriteria: function(allCriteria) {
            // takes flat list of criteria from API and maps into nested format, doing all transformations needed between API -> front-end
            /* let prepped = allCriteria.map(raw => {  // do prep work on raw values from API to make them easier to manage here
                let prep = Object.assign({}, raw);  // create new object to work with
                if (prep.parent === null && !prep.is_requirement && !prep.is_parent) {
                    console.warn('FOUND ONE THAT SHOULD BE is_parent BUT IS NOT');
                    console.warn(prep);
                }
                if (prep.parent === null) {  // this will be for top-level things and requirements
                    prep.parent = 'parent';  // for top-level criteria & requirements, we'll replace 'null' with 'parent' so easier to work with
                }
                prep.existing = true;  // mark these as existing so you know not to mess with their IDs when prepping to send back
                return prep
            });
            let separated = partition(prepped, function(crit) { return crit.is_requirement; }); // returns array of 2 arrays --> array[0] are criteria where is_requirement is true, array[1] are criteria where false (scoring criteria)
            let reqCriteria = separated[0];  // array of objects, should be ready to go as our requirements list/array
            let scoreCriteria = separated[1];  // flat array of scoring criteria, still needs to be nested
            let groupedScoring = groupBy(scoreCriteria, function(sc) { return sc.parent} );  // returns object of items grouped by parent id.  keys are parent ID, values are arrays of objects that share that parent ID
            console.log('from our buildNestedCriteria function, requirement criteria is:');
            console.log(reqCriteria);
            console.log('grouped scoring criteria is');
            console.log(groupedScoring);
            let parentGroups = groupedScoring['parent'];  // pulls out array of parent groups only (not yet nested)
            console.log(parentGroups);
            // now go through parent groups, find their array of child items and add them as nested items
            // must make sure you do this in way that Vue will be reactive on new object property 'items'
            let nestedScoringArray = parentGroups.map(parent => {  // returns a NEW array that is nested
                let parentNested = {...parent};  // start with this, which is safe because parent is not YET nested
                parentNested.items = groupedScoring[parent.id];  // get the array of nested items from results of groupBy function & store as nested on parent
                // CHECK THAT IT IS REACTIVE, OTHERWISE USE vm.$set()
                return parentNested;
            });  // this is our built-up nested scoring criteria... now we just set it to correct data property
            console.log('fully built nested array below');
            console.log(nestedScoringArray);
            // now set our correct data attributes */
            let processedCriteria = buildNestedCriteria(allCriteria);
            this.minimumReqs = processedCriteria.requirements;  // bada bing
            this.nestedCriteria = processedCriteria.scoring;  // bada boom :)
        },
        setDataFromApi: function(respData) {
            // set local data in consistent way after we get from API
            let allCriteria = respData.criteria_list;  // API will return it sorted by 'position'
            this.rfxHasExistingScores = respData.has_existing_score_sheets;  
            this.requirementsEnabled = respData.criteria_req_enabled;
            this.scoringEnabled = respData.criteria_scoring_enabled;
            this.criteriaShowVendors = respData.criteria_show_vendors;
            //if (this.overallEnabled == null) this.overallEnabled = respData.criteria_req_enabled || respData.criteria_scoring_enabled;  // if overallEnabled was null (or undefined) as it is during initial load, then set it here, otherwise don't set it from api response and let state get managed only locally
            this.overallEnabled = respData.criteria_req_enabled || respData.criteria_scoring_enabled;  // if overallEnabled was null (or undefined) as it is during initial load, then set it here, otherwise don't set it from api response and let state get managed only locally
            if (this.rfxHasExistingScores) this.showExistingScoreWarning();
            if (Array.isArray(allCriteria) && allCriteria.length > 0) {  // if we loaded criteria from API...
                this.buildNestedCriteria(allCriteria);  // send array to our function to build into nested format
            } // no need for else here... if we didn't get data, we don't want to overwrite the placeholder objects/array
            // at the end of it all, stash the last saved data IN FOR READY-FOR-SAVE format
            this.dataLastSaved = cloneDeep(this.prepAllDataForSave());  // cloneDeep just to make sure we don't mutate  
        },
        getCriteria: function() {
            this.loading = true;
            suppliosApi.rfx.listCriteria(this.rfxPk)
            .then(response => {
                console.log('got criteria from API, response below');
                console.log(response);
                this.setDataFromApi(response.data);
                this.initialLoadComplete = true;
            })
            .catch(error => this.handleError(error, 'Error while loading criteria from API'))
            .finally(() => this.loading = false)
        },
        updateCriteriaSettings: function() {
            // this only updates 2 booleans for criteria_req_enabled and criteria_scoring_enabled
            this.loadingSettings = true;
            this.$nextTick(() => {
                // wrap in next tick to allow data to be updated
                let updateObj = {...this.prepSettingsForApi()};  // copy it as good practice, use method for DRY purposes
                suppliosApi.rfx.updateCriteriaSettings(this.rfxPk, updateObj)
                    .then(response => {
                        console.log('response from updating settings');
                        console.log(response);
                        this.showSaveSuccess('Score Sheet settings updated');
                        this.setDataFromApi(response.data);  // data gets returned in same format as in getCriteria()
                    })
                    .catch(err => {
                        this.handleError(err, 'Error while updating criteria settings');
                    })
                    .then(() => {
                        this.loadingSettings = false;
                    })
            });
        },
        prepSettingsForApi: function() {
            // gets all settings data (some booleans) in one consistent format ready for API, just for DRY
            // returns an object ready for API
            return {
                criteria_req_enabled: this.requirementsEnabled,
                criteria_scoring_enabled: this.scoringEnabled,
                criteria_show_vendors: this.criteriaShowVendors,
            };
        },
        cleanCriteriaArrayForApi: function(array) {
            // generic cleaning function
            //var cloned = cloneDeep(array);  // clone the array so we don't mutate original - below should be doing that... .map does not mutate I think
            return array.map(item => {  // now mutate cloned array and return it
                var newObj = cloneDeep(item);
                newObj.rfx = this.rfxPk;  // changed 2020-10-20
                newObj.parent = null;  // remove all .parent attributes and let backend set them based on nested structure (since new parents don't have ID yet)
                if (!newObj.existing) {  // if new item (not existing) then we'll set ID to null, removing temporary ID
                    newObj.id = null;
                }
                if (has(newObj, 'items')) {
                    console.log('hello from inside cleanArray has items');
                    newObj.items = this.cleanCriteriaArrayForApi(newObj.items); // call this method recursively on nested items if it has it
                }
                return newObj;
            });
        },
        checkErrors: function() {
            // checks all your validity things, returns Array -- empty if no errors
            let errList = [];
            if (this.overallEnabled && this.allCriteriaDisabled) errList.push("To enable the Score Sheet you have to enable Minimum Qualifications and/or Scoring Criteria.");
            if (!this.allReqsValid) errList.push("There are issues with the Minimum Qualifications -- see highlighted errors.");
            if (!this.allWeightsValid) errList.push("There are issues with the weights for the Scoring Criteria -- see highlighted errors.");
            if (!this.allScoreCriteriaValid) errList.push("There are issues with the Scoring Criteria -- see highlighted errors.");
            return errList;
        },
        checkAllValidity: function() {
            // return OBJECT with keys of isValid (boolean) and errors (array of error strings)
            let everythingValid = true;  // set to false if you find something bad along the way
            let errList = [];
            if (this.overallEnabled && this.allCriteriaDisabled) {  // if user turned on 'use score sheet' but didn't add anything, show message
                errList.push("To enable the Score Sheet you have to enable Minimum Qualifications and/or Scoring Criteria.");
                everythingValid = false;
            } 
            if (this.requirementsEnabled && !this.allReqsValid) {
                errList.push("There are issues with the Minimum Qualifications -- see highlighted errors.");
                everythingValid = false;
            }
            if (this.scoringEnabled && !this.allWeightsValid) {
                errList.push("There are issues with the weights for the Scoring Criteria -- see highlighted errors.");
                everythingValid = false;
            }
            if (this.scoringEnabled && !this.allScoreCriteriaValid) {
                errList.push("There are issues with the Scoring Criteria -- see highlighted errors.");
                everythingValid = false;
            }
            if (!this.allSettingsValid) {
                // don't do anything here... this would only happen if overallEnabled is also on, so showing extra error message would be confusing
            }
            return {isValid: everythingValid, errors: errList};
        },
        startSave: function() {
            // main method to call save sequence -- check things, handles any issues, and calls real save if all good
            // check that criteria is valid locally, if not, show modal message box to ask what to do (fix it, or disable criteria are OK options for now)
            // return PROMISE that resolves to T/F
            let { isValid, errors } = this.checkAllValidity();
            console.log("result of checkAllValidity is below, first isValid, then errors");
            console.log(isValid);
            console.log(errors);
            if (isValid === true) return this.performSaveCriteria();
            //let theErrorList = this.checkErrors();
            let theErrorString = (errors.length > 0) ? errors.join(', ') : '';
            // now handle case where things are not valid... show message box
            let shouldContinue = this.$bvModal.msgBoxConfirm(`There are issues with the criteria setup. ${theErrorString} Fix now?  Or turn off decision criteria for now?`, {
                title: 'Issues Found',
                size: 'md',
                buttonSize: 'md',
                okVariant: 'primary',
                okTitle: "I'll Fix Now",
                cancelVariant: 'secondary',
                cancelTitle: 'Disable Criteria',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            })
            .then(value => {
                // value resolves to true if OK is clicked, false if cancel clicked, or null if modal closed
                // return a BOOLEAN:  true if we can proceed with whatever called this, false if we should not proceed (ie: don't continue with save/next)
                if (value === true) {  // handle OK (I'll fix now)
                    return false;  // return false, means caller should not proceed with whatever it was trying to do (save/next)
                }
                if (value === false) {  // handle cancel click (Disable Criteria), turn off criteria, then return something
                    // TODO:  this will still save all the bad criteria -- need things on backend that can validate / know about bad criteria!
                    this.performOverallDisable();  // use this method for DRY
                    return true;  // return true since we now can continue
                }
                if (value === null) {  // closed modal, treat it same as fix now
                    return false;  // return false, means caller should not proceed with whatever it was trying to do (save/next)
                }
            });
            return shouldContinue.then(proceed => {   // proceed is boolean value from msgBoxConfirm
                if (!proceed) {  // if not ok to proceed, don't call real save
                    console.log('from startSave, we found some problems so not calling save');
                    return false;  // return 
                }
                // otherwise continue calling real save
                console.log('from startSave, we found problems, but said to disable criteria, now calling real save');
                return this.performSaveCriteria();
            });
        },
        prepAllDataForSave: function() {
            // final formatter that pulls all data together in ready-for-api format
            let updateObject = {...this.prepSettingsForApi()};  // grab the currents settings (bunch of boolean flags), copy it as good practice, use method for DRY purposes
            // only update criteria if that type is enabled... otherwise don't even send anything to server
            if (this.requirementsEnabled) updateObject['requirements'] = this.cleanCriteriaArrayForApi(this.minimumReqs);
            if (this.scoringEnabled) updateObject['score_criteria'] = this.cleanCriteriaArrayForApi(this.nestedCriteria);
            return updateObject
        },
        performSaveCriteria: function() {
            // handles save/update via API -- must return a Promise that resolves to a Boolean if it was successful or not (caller can proceed or not)
            // if all criteria is disabled, then don't do any save and don't overwrite any data... just keep whatever was already in backend
            // only send criteria to API that you know is good data (all things valid)
            // but if you disable criteria, don't automatically overwrite existing criteria on backend in case there was valid stuff there that user wants to turn on later
            // if (this.allCriteriaDisabled) return Promise.resolve(true); // removing this so you can remove auto-save of settings... that was inconsistent UX and annoying... so need to allow user to save new settings if they turned everything off
            let updateObject = this.prepAllDataForSave();
            this.loading = true;
            console.log('from performSaveCriteria, this is what we are posting');
            console.log(updateObject);
            return suppliosApi.rfx.updateCriteria(this.rfxPk, updateObject)
            .then(response => {
                console.log('posted data, response below');
                console.log(response);
                this.showSaveSuccess('Score Sheet saved');
                /* this.$bvToast.toast('Criteria has been saved.', {
                    title: 'Success',
                    solid: true,
                }); */
                this.setDataFromApi(response.data);  // data gets returned in same format as in getCriteria()
                return true;
            })
            .catch(error => {
                this.handleError(error, 'Error while saving Decision Criteria');
                return false;
            })
            .then(wasSuccess => {
                this.loading = false;  // can skip this since we're reloading
                return wasSuccess;
            });
        },
        // NOTE: All these methods take an Array as input so you can use them on parent list or on child lists 
        itemTotal: function(list) {
            // returns total weights for items in a group/category
            if (!list) {
                return 0;
            }
            const reducer = (acc, currentItem) => {
                return acc + currentItem.weight;
            }
            return list.reduce(reducer, 0);  // make sure to set initialValue of 0 otherwise first item will be skipped
        },
        addItem: function(list, parentId) {
            let newPosition = list.length;  // new index/position number for item to be added
            const defaultNewItem = {
                name: '',
                id: 'newitem' + this.newItemId,
                existing: false,
                weight: 50,
                rating_type: 'FIVE',
                is_requirement: false,
                is_parent: false,
                position: newPosition,
                parent: parentId,  // need to make sure this is passed in
            };
            list.push(defaultNewItem);
            this.newItemId++;
        },
        addRequirement: function() {
            // add minimum requirement item -- similar to regular item, but different parameters
            let newPosition = this.minimumReqs.length;
            const defaultNewRequirement = {
                name: '',
                id: 'newreq' + this.newReqId,
                existing: false,
                weight: 0,  // can't set this to null, otherwise invalid on backend
                rating_type: 'BINARY',
                is_requirement: true,  // this is what tells us this is a requirement, not scoring item
                is_parent: false,
                position: newPosition,
                parent: 'parent',  // set to 'parent' which you will convert back to 'null' before sending back
            };
            this.minimumReqs.push(defaultNewRequirement);
            this.newReqId++;
        },
        addGroup: function() {
            let newPosition = this.nestedCriteria.length;  // new index/position number for item to be added
            const defaultNewGroup = {
                name: 'New Group ' + this.newGroupId.toString(),
                id: 'newgroup' + this.newGroupId,
                existing: false,
                weight: 50,
                rating_type: 'BINARY',  // makes no sense for group, but it is on model
                is_requirement: false,  // this is what tells us this is a requirement, not scoring item
                is_parent: true,  // tells us this holds other criteria items
                position: newPosition,
                parent: 'parent',  // set to 'parent' which you will convert back to 'null' before sending back
                items: [],
            };
            this.nestedCriteria.push(defaultNewGroup);
            this.newGroupId++;
        },
        removeGroup: function(list, index, catName) {
            // removes an entire group / category, but shows confirmation modal first... we don't do that just for each item, only groups
            // show confirmation modal, then if value is 'OK', we'll call normal removeItem method
            this.$bvModal.msgBoxConfirm('This will also delete all items in the group.  Are you sure?', {
                title: 'Delete ' + catName.toString() + '?',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'Yes, Delete',
                cancelTitle: 'No, Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            })
            .then(value => {  // 'value' passed from earlier promise will be 'true' if click OK, 'false' if cancel, 'null' if otherwise close modal
                if (value === true) {
                    this.removeItem(list, index, null);
                }
            })
            .catch(error => {
                this.handleError(error, 'Error while confirming deletion of Criteria Group');
            })
        },
        removeItem: function(list, index, parentId) {  // need parentId so we can call updateList appropriately
            let removedItem = list.splice(index, 1);
            this.updateList(list, parentId);
            console.log(removedItem);
        },
        singleRequirementValid: function(req) {
            // checks if a single requirement criteria is valid, checking multiple things, returns boolean (true if valid)
            return !!req.name && req.rating_type === 'BINARY' && req.is_requirement === true && req.is_parent === false;
        },
        singleCriteriaValid: function(criteria) {
            // checks if a single criteria is valid, checks multiple things - return boolean
            // currently only checks based on 'out of 100' type criteria, not used for requirements
            return !!criteria.name && !!criteria.rating_type && criteria.weight >= 0 && criteria.weight <= 100;
        },
        listCriteriaValid: function(list) {
            // checks if all criteria are filled in for a given list (array) - return boolean
            if (!Array.isArray(list)) return false;  // return false if its not an array (maybe throw error instead?)
            if (list.length < 1) return false;  // return false if no items in array
            return list.every(criteria => this.singleCriteriaValid(criteria));  // run each item through criteria checker
        },
        totalIsValid: function(list) {
            // checks if weights are valid for a given list (array) - return boolean
            let total = this.itemTotal(list);
            const targetTotal = this.groupTotalMax;
            if (total === targetTotal) {
                return true;
            } else {
                return false;
            }
        },
        splitWeights: function(list) {
            // splits item weights evenly for all items in a list
            // mutates the list (list item.weight) directly
            let itemCount = list.length;
            if (itemCount < 1) {  // if we don't have a list... don't proceed
                return
            }
            let evenWeightFloor = Math.floor(this.groupTotalMax / itemCount);
            let extraForLast = this.groupTotalMax - (itemCount * evenWeightFloor);
            list.forEach((item, index, array) => {
                item.weight = evenWeightFloor
                if (index === (array.length - 1)) {  // if this is the last item in array
                    item.weight = evenWeightFloor + extraForLast;
                }
            });
            // no need to return... we just mutated the list/array directly
        },

        itemOverallWeight: function(itemWeight, category) {
            // return % weight of this item in overall perspective -- category arg is the parent category object it is in
            // check that all weights are valid on complete sheet first
            return (itemWeight / 100) * (category.weight / 100);
        },
        percentMaker: function(value) {
            return new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 }).format(value);
        },
        handleSort: function(event, list, parentId) {
            // handles @sort events on draggable
            // parentId is the id of parent group/category
            this.updateList(list, parentId);  // must pass list to be dealt with
        },
        updateList: function(list, parentId) {  // handles various updates for the lists (positions, parent)
            list.forEach((item, index) => {
                item.position = index;  // mutates array directly, setting position to new index in array after sort action
                item.parent = parentId; // set parent ID to know where it is nested
                // no need to return anything... we're mutating array directly
            });
        },
        showExistingScoreWarning: function() {
            let warningText = "Some Vendor Responses have already been scored based on existing criteria.  If you add new criteria, you will have to re-score those responses.";
            let warningTitle = "Warning: Existing Scores";
            this.$bvModal.msgBoxOk(warningText, {
                title: warningTitle,
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                centered: true,
            }) // this returns a promise, so you can do .then(value => ..... value will be true when user clicks on 'ok')
        },
        handleUnsavedData: function() {
            // show message box to confirm if user wants to save changes or discard - called from okToContinue method 
            // return a PROMISE that resolves to Boolean:  true if OK to proceed, false if should not proceed
            return this.$bvModal.msgBoxConfirm('You have unsaved changes to the Score Sheet.  Do you want to save them now?', {
                title: 'Unsaved Changes',
                size: 'md',
                buttonSize: 'sm',
                okVariant: 'primary',
                okTitle: 'Yes, Save Changes',
                cancelVariant: 'secondary',
                cancelTitle: 'No, Discard Changes',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            })
                .then(clickOk => {  // value will be true (save changes, then proceed) or false (discard changes & proceed) or null (closed modal - so don't proceed or save)
                    if (clickOk === true) {
                        // handle save changes
                        return this.startSave();  // just return the Boolean value from save promise chain - if it returns true then save was good and we'll proceed.  if it returns false we won't proceed
                    }
                    if (clickOk === false) {
                        this.proceedUnsavedChanges = true;  // must set this so we don't get stuck in endless loop... this is checked during public method this.canLeave()
                        return true;  // true means we proceed without saving changes, don't call save
                    }
                    // now handle null/undefined case (user closed modal - don't save, don't proceed)
                    return false;  
                })
        },
        hasUnsavedCheck: function() {
            // method that checks current data vs. last saved data (last received from API)
            // return Boolean, not in promise, where true = has unsaved changes
            // IF INITIAL DATA NOT LOADED YET, RETURN FALSE 
            if (!this.initialLoadComplete) return false;  
            let lastSaved = this.dataLastSaved;
            let currentData = this.prepAllDataForSave();
            let isUnsaved = !isEqual(lastSaved, currentData);
            console.log("From hasUnsavedCheck, logging isUnsaved, lastSaved, current");
            console.log(isUnsaved);
            console.log(lastSaved);
            console.log(currentData);
            //window.alert("unsaved changes? " + isUnsaved.toString())
            return isUnsaved;
        },
        confirmOverallDisable: function() {
            // show message / confirmation modal to confirm user wants to turn off oveallEnabled -- trigger this only when there are valid criteria in the list
            return this.$bvModal.msgBoxConfirm('You already added some decision criteria.  Are you sure you want to disable the Score Sheet?', {
                title: 'Disable Score Sheet?',
                size: 'md',
                buttonSize: 'sm',
                okVariant: 'primary',
                okTitle: 'Yes, Disable Score Sheet',
                cancelVariant: 'secondary',
                cancelTitle: 'No, Keep Score Sheet',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            })
                .then(clickOk => {  // you probably don't need this .then() chain, but it makes the return logic more clear...
                    if (clickOk === true) {
                        // continue with turning off score sheet
                        return true;  
                    }
                    if (!clickOk) return false;  // if clickOk is false, or null or undefined (user closed confirm modal) then return false and don't continue with disable score sheet
                    return false;  // fallback case... shouldn't happen really
                })
        },
        performOverallDisable: function() {
            // call this to disable all score sheet functionality, separating out for DRY
            this.criteriaShowVendors = false;  // force this to be false if overallEnabled is false 
            this.requirementsEnabled = false;  // force this off
            this.scoringEnabled = false;  // force this off
            this.overallEnabled = false;  // updates the value of selector, through here, not through v-model
        },
        handleSettingsChange: function(reqs, scoring, overall, showVendors) {
            // one consolidated method for handling state change to settings... doing it in one method to keep logic together since there are so many dependencies
            // checkboxes for all these things should have their event handlers go here
            // all args are boolean -- if a checkbox doesn't set it directly, pass in null (SPECIFICALLY NULL, not false), and this will get value from local data
            // FYI, null == undefined (is a true statement), so you could use double equals to catch both if needed, or != for the opposite case 
            // FIRST, set/get current values for 3 main settings, then update them if you passed in value for overall
            console.log('From handleSettingsChange, incoming args:');
            console.log([reqs, scoring, overall, showVendors]);
            if (reqs != null) this.requirementsEnabled = reqs;  // set to value passed in if there was one
            if (scoring != null) this.scoringEnabled = scoring;  // set to value passed in if there was one
            if (showVendors != null) this.criteriaShowVendors = showVendors;  // set to value passed in if there was one
            // NEXT, update those values if you passed in something for overall 
            if (overall != null) {
                if (overall === false) {  // if here, it means user just turned off overallEnabled
                    let showingSomeRequirements = this.requirementsEnabled && this.hasAnyRequirements;  // only trigger confirmation modal if there are requirements, and if they're showing
                    let showingSomeScoreCriteria = this.scoringEnabled && this.hasAnyScoreCriteria;
                    if (showingSomeRequirements || showingSomeScoreCriteria) {
                        let continueWithDisablePromise = this.confirmOverallDisable();  // this returns a promise resolving to boolean, true means continue with disable, false means do not disable
                        continueWithDisablePromise.then(shouldContinue => {  // shouldContinue is the boolean from the promise
                            if (shouldContinue) this.performOverallDisable();
                            // nothing else after this... if user didn't want to continue after having criteria already entered, don't modify anything
                        })
                    } else {  // now handle case where there were no criteria entered, so no need to show confirmation message... (!hasAny...)
                        this.performOverallDisable();
                    }
                }
                if (overall === true) {
                    this.overallEnabled = true;  // updaes the value of selector, thorugh here, not through v-model
                    console.log('just set overallEnabled to true');
                }
            }
            // now that you're done, call update method to update backend -- there should be something to update since this method was called in the first place
            //this.updateCriteriaSettings();  // this has nextTick built in so it should use the updated data... but if not, then change it to accept args 
        }
    }
}
</script>

<style lang="scss">
.list-group-item.item.drag-handle,
.criteria-group .group-drag-handle,
.sortable-drag {
    cursor: move;
}

.spl-inputs-minimal .empty-value {
    color: var(--primary-300) !important;
}

#rfx-manage-criteria-overall-settings-row {
    transition: margin 0.4s ease;
    &:not(.scoring-active) {
        margin-top: 3rem;
        margin-bottom: 3rem;
    }
    &.scoring-active {
        margin-top: 0;
        margin-bottom: 0;
    }
}


</style>